import { CommonMessage } from '@frontend/lang';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { reportBug } from '../index';
import { goBackAndResetError } from './error-boundary-navigation';
import './sentry-error-boundary.css';

type ReportState = 'idle' | 'open' | 'submitting' | 'success' | 'error';

export interface PlatformErrorFallbackProps {
    error: Error;
    eventId: string;
    resetError: () => void;
}

export function PlatformErrorFallback({ error: _error, eventId, resetError }: PlatformErrorFallbackProps) {
    const intl = useIntl();

    const [reportState, setReportState] = useState<ReportState>('idle');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setReportState('submitting');
        const result = await reportBug({ message, name, email, associatedEventId: eventId });
        setReportState(result ? 'success' : 'error');
    }

    return (
        <div className='seb-overlay'>
            <div className='seb-platform-card'>
                <div className='seb-platform-header'>
                    <div className='seb-platform-icon'>⚠</div>
                    <div>
                        <h1 className='seb-title'>
                            <FormattedMessage
                                id='SentryErrorBoundary.Title'
                                description='Title shown on the error boundary screen'
                                defaultMessage='Oops!'
                            />
                        </h1>
                        <p className='seb-description'>
                            <FormattedMessage
                                id='SentryErrorBoundary.Description'
                                description='Body text on the error boundary screen, apologising and prompting the user to go back or contact support'
                                defaultMessage='It seems like there is something wrong with the page you requested. We are very sorry for the inconvenience. You can go back where you came from and try again. Or if this keeps happening, please contact us.'
                            />
                        </p>
                    </div>
                </div>

                <div className='seb-platform-body'>
                    {reportState === 'success' ? (
                        <p className='seb-success-msg'>
                            {CommonMessage.ERRORS.BUG_REPORT.FORM_SUCCESS((chunks) => (
                                <strong>{chunks}</strong>
                            ))}
                        </p>
                    ) : (
                        <>
                            {(reportState === 'open' || reportState === 'submitting' || reportState === 'error') && (
                                <form
                                    id='platform-fb-form'
                                    onSubmit={handleSubmit}
                                    className='seb-form'>
                                    <div className='seb-platform-form-row'>
                                        <div className='seb-field'>
                                            <label
                                                htmlFor='platform-fb-name'
                                                className='seb-label'>
                                                {CommonMessage.FORMS.NAME}
                                            </label>
                                            <input
                                                id='platform-fb-name'
                                                type='text'
                                                className='seb-input'
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder={intl.formatMessage({
                                                    id: 'SentryErrorBoundary.Form.NamePlaceholder',
                                                    description: 'Placeholder for the name field in the bug report form',
                                                    defaultMessage: 'Your name (optional)'
                                                })}
                                                disabled={reportState === 'submitting'}
                                            />
                                        </div>
                                        <div className='seb-field'>
                                            <label
                                                htmlFor='platform-fb-email'
                                                className='seb-label'>
                                                {CommonMessage.ERRORS.BUG_REPORT.FORM_EMAIL_LABEL}
                                            </label>
                                            <input
                                                id='platform-fb-email'
                                                type='email'
                                                className='seb-input'
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder={intl.formatMessage({
                                                    id: 'SentryErrorBoundary.Form.EmailPlaceholder',
                                                    description: 'Placeholder for the email field in the bug report form',
                                                    defaultMessage: 'your@email.com (optional)'
                                                })}
                                                disabled={reportState === 'submitting'}
                                            />
                                        </div>
                                    </div>
                                    <div className='seb-field'>
                                        <label
                                            htmlFor='platform-fb-message'
                                            className='seb-label'>
                                            {CommonMessage.ERRORS.BUG_REPORT.FORM_MESSAGE_LABEL} <span className='seb-required'>*</span>
                                        </label>
                                        <textarea
                                            id='platform-fb-message'
                                            className='seb-textarea'
                                            rows={4}
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder={intl.formatMessage({
                                                id: 'SentryErrorBoundary.Form.MessagePlaceholder',
                                                description: 'Placeholder for the message field in the bug report form',
                                                defaultMessage: 'Describe what you were doing when the error occurred...'
                                            })}
                                            required
                                            disabled={reportState === 'submitting'}
                                        />
                                    </div>
                                    {reportState === 'error' && <p className='seb-error-msg'>{CommonMessage.ERRORS.BUG_REPORT.FORM_ERROR}</p>}
                                </form>
                            )}
                        </>
                    )}
                </div>

                <div className='seb-footer'>
                    <button
                        type='button'
                        className='seb-btn seb-btn-primary'
                        onClick={() => goBackAndResetError(resetError)}>
                        ← {CommonMessage.BUTTONS.GO_BACK}
                    </button>

                    {reportState === 'idle' && (
                        <button
                            type='button'
                            className='seb-btn seb-btn-outline'
                            onClick={() => setReportState('open')}>
                            {CommonMessage.ERRORS.BUG_REPORT.BUTTON}
                        </button>
                    )}

                    {(reportState === 'open' || reportState === 'submitting' || reportState === 'error') && (
                        <>
                            <button
                                type='submit'
                                form='platform-fb-form'
                                disabled={reportState === 'submitting' || !message.trim()}
                                className='seb-btn seb-btn-secondary'
                                onClick={handleSubmit}>
                                {reportState === 'submitting' && (
                                    <span
                                        className='seb-spinner-sm'
                                        role='status'
                                    />
                                )}
                                {reportState === 'submitting' ? CommonMessage.ERRORS.BUG_REPORT.FORM_SENDING : CommonMessage.ERRORS.BUG_REPORT.FORM_SUBMIT}
                            </button>
                            <button
                                type='button'
                                className='seb-btn-link'
                                onClick={() => setReportState('idle')}
                                disabled={reportState === 'submitting'}>
                                {CommonMessage.BUTTONS.CANCEL}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
